<template>
  <sp-card :variant="cardVariant" :elevation="cardElevation" class="entry-card" :class="modifierClasses">
    <sp-card-content class="card-content">
      <sp-row align-content="space-between" height="100%" columngap="0" no-gutters :rowgap="0">
        <sp-container>
          <sp-text class="card-title" :type="cardTitleTypeScale">
            {{ cardTitle }}
          </sp-text>
          <sp-footnote class="card-subtitle">{{ cardSubtitle }}</sp-footnote>
        </sp-container>

        <template v-if="isSmallView">
          <sp-button class="show-more" variant="text-inline" size="small" no-wrap @click="showMoreSlots">
            {{ slotsCount }} appts
          </sp-button>
        </template>

        <div v-else class="slots-container">
          <sp-slot
            v-for="(slot, index) in visibleSlots"
            :key="index"
            :entry.prop="slot"
            :variant="slotVariant"
          ></sp-slot>

          <sp-button
            v-if="hasMoreSlots"
            class="more-button"
            size="small"
            :variant="moreButtonVariant"
            no-wrap
            block
            @click="showMoreSlots"
          >
            {{ moreSlotsCount }} more
            <sp-icon name="triangle-down-filled" size="small" />
          </sp-button>
        </div>
      </sp-row>
    </sp-card-content>
  </sp-card>
</template>

<script setup>
import moment from "moment";
import { computed } from "vue";

const emit = defineEmits(["select-slot", "show-more-slots"]);

const props = defineProps({
  /**
   * The entry object to display.
   *
   * @type {Object}
   * @property {String} date The date of the entry.
   * @property {Array} slots The slots for the entry.
   * @property {String} slots.start_time The start time of the slot.
   * @property {String} slots.booking_url The URL to book the slot.
   * @property {String} slots.visit_type_categories_description The description of the visit type.
   *
   * @example
   * {
   *  date: "2021-09-01",
   *  slots: [{
   *   start_time: "2021-09-01T09:00:00Z",
   *   booking_url: "https://example.com",
   *   visit_type_categories_description: "New Patient"
   *  }],
   * }
   *
   * @required true
   */
  entry: {
    type: Object,
    required: true,
  },
  /**
   * Whether the card is being displayed on a mobile device.
   * We use the parent object to determine this in order to avoid having too many breakpoint listeners.
   *
   * @type {Boolean}
   * @default false
   */
  isSmallView: {
    type: Boolean,
    default: false,
  },
  /**
   * The maximum number of slots to display.
   *
   * This is only used when the card is being displayed on a non-mobile device.
   * Mobile devices will always display a summary.
   * The rest of the slots will be hidden and a "Show more" button will be displayed.
   *
   * @type {Number}
   * @default 2
   */
  maxSlots: {
    type: Number,
    default: 2,
  },
  columngap: {
    type: String,
    default: "0",
  },
  slotVariant: {
    type: String,
    default: "outlined",
  },
  moreButtonVariant: {
    type: String,
    default: "outlined",
  },
  cardElevation: {
    type: Number,
    default: undefined,
    validator: (value) => [1, 2, 3].includes(value),
  },
});

const date = computed(() => moment(props.entry.date));

const cardVariant = computed(() => (props.isSmallView ? "outlined" : "elevated"));
const cardTitle = computed(() => date.value.format("MMM D"));
const cardTitleTypeScale = computed(() => (props.isSmallView ? "body-bold" : "h4"));
const cardSubtitle = computed(() => date.value.format(props.isSmallView ? "ddd" : "dddd"));
const modifierClasses = computed(() => ({ "--is-small-view": props.isSmallView }));

const slots = computed(() => props.entry.slots);
const slotsCount = computed(() => slots.value.length);
const visibleSlotsCount = computed(() => (props.isSmallView ? 0 : props.maxSlots));
const visibleSlots = computed(() => slots.value.slice(0, visibleSlotsCount.value));
const moreSlotsCount = computed(() => slotsCount.value - visibleSlots.value.length);
const hasMoreSlots = computed(() => moreSlotsCount.value > 0);

function showMoreSlots() {
  emit("show-more-slots", { date: props.entry.date });
}
</script>

<style lang="scss" scoped>
.entry-card {
  --column-gap: v-bind(columngap);
  --height-default: 16rem;
  --height-small: 8.5rem;
  --sp-comp-slot-cards-entry-card-padding-block: var(--sp-ref-spacing-6) var(--sp-ref-spacing-10);
  --sp-comp-slot-cards-entry-card-padding-inline: var(--sp-ref-spacing-12);
  --sp-comp-card-padding-block: var(--sp-comp-slot-cards-entry-card-padding-block);
  --sp-comp-card-padding-inline: var(--sp-comp-slot-cards-entry-card-padding-inline);
  --sp-comp-card-border: var(--sp-comp-slot-cards-border, 1px solid var(--sp-sys-color-outline-variant));
  --sp-comp-card-content-height: calc(var(--sp-comp-slot-cards-height, 16rem) - var(--padding-block) * 2);
  --sp-comp-card-border-radius: var(--sp-comp-slot-cards-border-radius, var(--sp-ref-border-radius-xs));
  --sp-card-root-height: var(--height-default);
  --sp-comp-card-content-height: var(--height-default);

  width: var(--sp-comp-slot-cards-card-width, 12rem);

  &[variant="outlined"] {
    --border-color: var(--sp-comp-slot-cards-outlined-border-color, var(--sp-sys-color-primary));
    --border-width: var(--sp-comp-slot-cards-outlined-border-width, 2px);
    --sp-comp-card-outlined-border: var(--border-width) solid var(--border-color);
    --sp-comp-card-content-height: var(--sp-comp-slot-cards-height, var(--height-default));
  }

  sp-button[variant="outlined"] {
    --sp-comp-button-small-padding: 0 var(--sp-ref-spacing-2);
  }

  &.--is-small-view {
    --padding-block: var(--sp-ref-spacing-8);
    --padding-inline: var(--padding-block);
    --sp-comp-card-content-height: var(--sp-comp-slot-cards-height-small-view, var(--height-small));
    --sp-card-root-height: var(--height-small);
    width: calc((100% / 3) - var(--column-gap));

    .show-more {
      --sp-comp-button-font-short: var(--sp-sys-typescale-body-semi-bold-short);
    }
  }
}

sp-card-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
}

sp-button {
  &.more-button {
    --sp-comp-button-font-weight: var(
      --sp-comp-slot-cards-more-button-font-weight,
      var(--sp-sys-typescale-body-font-weight)
    );
  }
}

sp-text {
  &.card-title {
    --sp-sys-typescale-h4-short: var(
      --sp-comp-slot-cards-title-font-short,
      var(--sp-sys-typescale-h4-font-weight) var(--sp-sys-typescale-h4-font-size) /
        var(--sp-sys-typescale-h4-line-height) var(--sp-sys-typescale-h4-font-family)
    );
  }
}

sp-footnote {
  &.card-subtitle {
    --sp-sys-typescale-footnote-short: var(
      --sp-comp-slot-cards-subtitle-font-short,
      var(--sp-sys-typescale-footnote-font-weight) var(--sp-sys-typescale-footnote-font-size) /
        var(--sp-sys-typescale-footnote-line-height) var(--sp-sys-typescale-footnote-font-family)
    );
  }
}

.slots-container {
  display: grid;
  grid-template-rows: repeat(3, var(--sp-comp-slot-cards-slot-height, var(--sp-ref-spacing-18)));
  row-gap: var(--sp-comp-slot-cards-slot-row-gap, var(--sp-ref-spacing-4));
  width: 100%;
  align-items: end;
}
</style>
